import React, { useState, useRef, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Send, ScanSearch, Scale, Route, ShieldAlert, Lock, Loader2, Cpu, Globe,
  MapPin, Copy, ClipboardCheck, ShieldCheck, ChevronDown, XCircle, CheckCircle2,
  User, MessageSquare, Square, Trash2, Check, MessageSquareLock, Paperclip,
  Hash, Timer,
} from "lucide-react";
import { api } from "../lib/api";
import { useConversationHistory } from "../lib/useConversationHistory";
import {
  conversationIdFromLocationState,
  conversationToOpenOnMount,
  newConversationId,
  nextConversationLoadError,
  shouldResetChatAfterFailedLoad,
} from "../lib/conversationHistoryUi";
import { GATEWAY_CHAT_EXAMPLES } from "../lib/gatewayChatExamples";
import { useAuth } from "../App";
import { AttachmentChip } from "./AttachmentChip";
import { ComposerAttachments } from "./ComposerAttachments";
import { HeldResponseNotice } from "./HeldResponseNotice";
import {
  beginReplaceAt as beginReplaceAtHelper,
  clearReplaceIntent,
  handleComposerFilesSelected,
} from "../lib/composerUpload";
import { ActionBadge, Pill, Btn, DataTrace } from "./ui";

const TONE_META = {
  pass: { label: "Should pass", color: "#10B981" },
  mask: { label: "Should mask", color: "#06B6D4" },
  strict: { label: "Highly sensitive", color: "#14B8A6" },
};

const fmtTs = (iso) => (iso ? new Date(iso).toLocaleString("en-GB", { hour12: false }) : "");

// Strip the server-side "===== Attached file: …" blocks so the chat bubble shows
// only what the employee typed; the files are shown as chips instead.
const typedOnly = (text) => {
  if (!text) return "";
  const i = text.indexOf("===== Attached file:");
  return (i === -1 ? text : text.slice(0, i)).trim();
};

// Canonical 4-step trace from a Gateway Chat message.
function traceFromResult(msg, respText, streaming) {
  const r = msg.steps.response;
  const noProvider = r.blocked || r.held;
  const note = r.blocked ? "request blocked" : "request held — no compliant provider";
  const atts = msg.attachmentsSent || (msg.steps && msg.steps.attachments) || [];
  let input = msg.prompt;
  if (!(input && input.trim()) && atts.length) {
    const names = atts.map((a) => a.filename || a.original_filename).filter(Boolean).join(", ");
    input = `No typed text — ${atts.length} attachment${atts.length > 1 ? "s" : ""} (${names}). Extracted file text was scanned & masked; see Step 2.`;
  }
  const entities = (msg.steps.detection && msg.steps.detection.entities) || [];
  const sent = noProvider
    ? { applicable: false, note }
    : r.masked_count > 0 && r.masked_prompt
    ? { applicable: true, text: r.masked_prompt, unchanged: false }
    : r.masked_count > 0
    ? { applicable: true, text: "(provider payload not retained in history view)", withheld: true }
    : { applicable: true, text: input, unchanged: true };
  const received = noProvider
    ? { applicable: false, note }
    : respText
    ? { applicable: true, text: respText }
    : { applicable: true, text: "(raw response not retained in history view)", withheld: true };
  const final = noProvider
    ? { applicable: false, note }
    : {
        applicable: true,
        text: msg.steps.response.final != null
          ? msg.steps.response.final
          : (msg.steps.response.content_status === "final_unavailable" || msg.steps.response.final == null
            ? "Final not available"
            : msg.steps.response.final),
        status: msg.verified ? "verified" : "unverified",
        verifiedBy: msg.verified_by,
        verifiedAt: msg.verified_at,
      };
  return { input, sent, received, final, streaming, entities: entities.map((e) => e && e.matched_text).filter(Boolean) };
}

function traceToPlain(t) {
  const na = (s) => `not applicable — ${s.note}`;
  const sent = t.sent.applicable ? t.sent.text + (t.sent.unchanged ? "  [sent unchanged]" : "") : na(t.sent);
  const received = t.received.applicable ? t.received.text || "—" : na(t.received);
  let final = t.final.applicable ? t.final.text || "—" : na(t.final);
  if (t.final.applicable) final += t.final.status === "verified" ? "\n\nVerification: Verified" : "\n\nVerification: Unverified — not yet confirmed";
  return [
    "LucidGate — Request trace", "",
    "STEP 1 — INPUT (prompt exactly as typed)", t.input || "—", "",
    "STEP 2 — SENT (payload the AI provider received)", sent, "",
    "STEP 3 — RECEIVED (raw AI response before verification gate)", received, "",
    "STEP 4 — FINAL OUTPUT (what the user can use)", final,
  ].join("\n");
}

/* ── Right pane: Observability & Trace Inspector ─────────────────────────── */
function Inspector({ item, streamText, streaming }) {
  if (!item) {
    return (
      <div data-testid="inspector-empty" className="flex flex-1 flex-col items-center justify-center p-8 text-center">
        <div className="flex h-14 w-14 items-center justify-center rounded-2xl border border-subtle bg-elevated">
          <Route className="h-7 w-7 text-slate-600" />
        </div>
        <p className="mt-4 font-display text-sm font-bold text-slate-300">No message selected</p>
        <p className="mt-1 max-w-xs text-xs text-slate-500">
          Send a prompt or click any message on the left to inspect its live 4-step pipeline trace, detection, policy and sovereign routing.
        </p>
      </div>
    );
  }

  const { msg, live } = item;
  const s = msg.steps;
  const r = s.response;
  const respRaw = live ? streamText : r.text || "";
  const showStreaming = live && streaming;
  const trace = traceFromResult(msg, respRaw, showStreaming);
  const [copied, setCopied] = useState(false);
  const copyTrace = async () => {
    try { await navigator.clipboard.writeText(traceToPlain(trace)); setCopied(true); setTimeout(() => setCopied(false), 1600); } catch (e) { /* noop */ }
  };
  const ref = msg.id ? String(msg.id).slice(0, 8) : live ? "live" : "—";

  return (
    <>
      <div data-testid="inspect-pane-header" className="flex items-center justify-between gap-2 border-b border-subtle bg-surface/80 px-4 py-3 backdrop-blur">
        <div className="min-w-0">
          <div className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-slate-500">
            <ScanSearch className="h-3.5 w-3.5" /> Observability Inspector
          </div>
          <div className="mt-0.5 flex items-center gap-2 font-mono text-[10px] text-slate-500">
            <span className="flex items-center gap-1"><Hash className="h-3 w-3" />{ref}</span>
            {r.latency_ms ? <span className="flex items-center gap-1"><Timer className="h-3 w-3" />{r.latency_ms} ms</span> : null}
          </div>
        </div>
        <button
          data-testid="copy-full-trace"
          onClick={copyTrace}
          className="flex shrink-0 items-center gap-1.5 rounded-lg border border-subtle px-2.5 py-1.5 font-mono text-[10px] uppercase tracking-widest text-slate-300 hover:border-brand/50 hover:text-brand"
        >
          {copied ? <ClipboardCheck className="h-3 w-3 text-allow" /> : <Copy className="h-3 w-3" />}
          {copied ? "Copied" : "Copy trace"}
        </button>
      </div>

      <div className="custom-scrollbar flex-1 space-y-4 overflow-y-auto p-4">
        {/* Outcome summary */}
        <div className="rounded-xl border border-subtle bg-elevated/60 p-3">
          <div className="mb-2 flex flex-wrap items-center gap-2">
            <ActionBadge action={s.policy.overall_applied} small />
            {r.masked_count > 0 && <Pill tone="mask"><Lock className="h-3 w-3" /> {r.masked_count} tokenized</Pill>}
            {s.routing.chosen
              ? (s.routing.chosen.eu_sovereign ? <Pill tone="eu"><Globe className="h-3 w-3" /> EU · {s.routing.chosen.name}</Pill> : <Pill tone="muted"><Cpu className="h-3 w-3" /> {s.routing.chosen.name}</Pill>)
              : <Pill tone="block">no provider</Pill>}
            {s.routing.us_excluded && <Pill tone="eu"><Lock className="h-3 w-3" /> US excluded</Pill>}
          </div>
          {/* Legend for the trace marking */}
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 font-mono text-[10px] uppercase tracking-wider text-slate-400">
            <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded border border-warn/50 bg-warn/25" /> detected</span>
            <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded border border-mask/50 bg-mask/25" /> tokenized / sent</span>
            <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded border border-allow/50 bg-allow/25" /> restored for you</span>
          </div>
        </div>

        {/* Card 1 — trace */}
        <div>
          <div className="mb-2 flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-slate-500">
            <Route className="h-3.5 w-3.5" /> 4-Step pipeline trace
          </div>
          <DataTrace trace={trace} testid="gateway-data-trace" />
        </div>

        {/* Card 2 — detection */}
        <div className="rounded-xl border border-subtle bg-surface/60 p-4">
          <div className="mb-2 flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-slate-500">
            <ScanSearch className="h-3.5 w-3.5" /> Sensitive entity detection
          </div>
          {s.detection.clean ? (
            <div className="flex items-center gap-1.5 text-xs text-allow"><CheckCircle2 className="h-3.5 w-3.5" /> No sensitive data detected</div>
          ) : (
            <div className="space-y-2">
              <div className="flex flex-wrap gap-1.5">
                {s.detection.categories.map((c) => <Pill key={c.id} tone="warn">{c.label}</Pill>)}
              </div>
              {(s.detection.entities || []).length > 0 && (
                <div className="flex flex-wrap gap-1.5 border-t border-subtle pt-2">
                  {s.detection.entities.map((e, i) => (
                    <span key={i} className="rounded-md border border-subtle bg-elevated px-2 py-0.5 font-mono text-[10px] text-slate-300">
                      <span className="text-slate-500">{e.label}:</span> {e.matched_text}
                    </span>
                  ))}
                </div>
              )}
              {s.detection.categories.some((c) => c.id === "infra_secrets") && (
                <div data-testid="infra-legend" className="mt-2 rounded-md border border-subtle bg-elevated/50 p-2">
                  <div className="mb-1 font-mono text-[9px] uppercase tracking-widest text-slate-500">Infrastructure sub-types</div>
                  <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[10px] text-slate-400">
                    <span><b className="text-slate-300">IP address</b> — server/host IP</span>
                    <span><b className="text-slate-300">SSH key</b> — key file / .ssh path</span>
                    <span><b className="text-slate-300">URL / endpoint</b> — internal API/URL</span>
                    <span><b className="text-slate-300">user@host</b> — login target</span>
                    <span><b className="text-slate-300">File / repo path</b> — filesystem path</span>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Card 3 — policy */}
        <div className="rounded-xl border border-subtle bg-surface/60 p-4">
          <div className="mb-2 flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-slate-500">
            <Scale className="h-3.5 w-3.5" /> Layered policy · strictest wins
          </div>
          {s.policy.decisions.length === 0 ? (
            <div className="flex items-center gap-1.5 text-xs text-slate-500">
              No category rules triggered · resolved <ActionBadge action={s.policy.overall_applied} small testid="overall-decision" />
            </div>
          ) : (
            <div className="space-y-1.5">
              {s.policy.decisions.map((d) => (
                <div key={d.category} className="flex items-center justify-between gap-2">
                  <span className="truncate text-[11px] text-slate-300">{d.label}</span>
                  <span className="flex items-center gap-1.5">
                    <ActionBadge action={d.applied_action} small />
                    <span className="font-mono text-[9px] uppercase text-slate-600">{d.deciding_layer}</span>
                  </span>
                </div>
              ))}
              <div className="flex items-center gap-1.5 border-t border-subtle pt-2 text-[11px] text-slate-500">
                Resolved <ActionBadge action={s.policy.overall_applied} small testid="overall-decision" />
              </div>
            </div>
          )}
        </div>

        {/* Card 4 — routing */}
        <div className="rounded-xl border border-subtle bg-surface/60 p-4">
          <div className="mb-2 flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-slate-500">
            <Route className="h-3.5 w-3.5" /> Sovereign routing & telemetry
          </div>
          {s.routing.chosen ? (
            <div className="space-y-1.5">
              <div className="flex items-center gap-1.5 text-sm text-white" data-testid="chosen-provider">
                <Cpu className="h-4 w-4 text-brand" /> {s.routing.chosen.name}
              </div>
              <div className="flex items-center gap-1.5 font-mono text-[10px] text-slate-500">
                <MapPin className="h-3 w-3" /> {s.routing.chosen.jurisdiction}
              </div>
              <div className="flex flex-wrap items-center gap-1.5">
                {s.routing.chosen.eu_sovereign ? <Pill tone="eu"><Globe className="h-3 w-3" /> EU sovereign</Pill> : <Pill tone="muted">{s.routing.chosen.region}</Pill>}
                {s.routing.chosen.simulated && <Pill tone="warn">simulated</Pill>}
              </div>
            </div>
          ) : (
            <div className="text-xs text-block">No provider contacted.</div>
          )}
          {s.routing.us_excluded && (
            <div className="mt-2 flex items-center gap-1 rounded-md border border-eu/30 bg-eu/10 px-2 py-1 text-[10px] text-eu">
              <Lock className="h-3 w-3" /> US providers excluded
            </div>
          )}
          {s.routing.reason && <p className="mt-2 text-[11px] leading-relaxed text-slate-500">{s.routing.reason}</p>}
        </div>
      </div>
    </>
  );
}

export default function GatewayChat() {
  const { session } = useAuth();
  const sector = session.tenant.sector;
  const [prompt, setPrompt] = useState("");
  const [conversationId, setConversationId] = useState(newConversationId);
  const [live, setLive] = useState(null);
  const [streamText, setStreamText] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [verifyingId, setVerifyingId] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [showConvs, setShowConvs] = useState(false);
  const [confirmDelConv, setConfirmDelConv] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [focusedKey, setFocusedKey] = useState(null);
  const [mobileTab, setMobileTab] = useState("chat");
  const endRef = useRef(null);
  const fileRef = useRef(null);
  /** Attachment id to replace on next successful upload; cancel/fail leaves chip intact. Never auto-sends. */
  const replaceIdRef = useRef(null);
  const runIdRef = useRef(0);
  const abortRef = useRef(null);
  const liveRef = useRef(null);

  const location = useLocation();
  const navigate = useNavigate();
  const activeConvKey = `lg_active_conv_${session.user.id}`;
  const openConversationId = conversationIdFromLocationState(location.state);
  const {
    messages, setMessages, historyHasMore, historyLoading,
    loadConversation: loadConvHistory, loadOlderMessages: loadOlder,
    resetHistory,
  } = useConversationHistory({
    tenantId: session.tenant.id,
    activeConvKey,
    abortRef,
    runIdRef,
  });
  const hasThread = messages.length > 0 || !!live;

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, live, streamText]);

  const refreshConversations = () => {
    api.conversations(session.tenant.id, { user_id: session.user.id }).then(setConversations).catch(() => {});
  };
  useEffect(() => { refreshConversations(); /* eslint-disable-next-line */ }, []);

  useEffect(() => {
    const choice = conversationToOpenOnMount(openConversationId, localStorage.getItem(activeConvKey));
    if (choice.consumeLocationState) navigate({ pathname: location.pathname, search: location.search, hash: location.hash }, { replace: true, state: null });
    if (choice.conversationId) loadConversation(choice.conversationId); /* eslint-disable-next-line */
  }, []);

  const loadConversation = async (cid) => {
    setShowConvs(false);
    await loadConvHistory(cid, {
      onLiveReset: () => {
        setLive(null); liveRef.current = null; setStreamText(""); setStreaming(false); setLoading(false);
      },
      onOpened: (id, msgs) => {
        setError((prev) => nextConversationLoadError(prev, { accepted: true, ok: true }));
        setConversationId(id);
        setFocusedKey(msgs.length ? msgs[msgs.length - 1].id : null);
      },
      onError: (_msg, meta) => {
        setError((prev) => nextConversationLoadError(prev, { accepted: true, ok: false }));
        if (meta?.status !== 404 || !shouldResetChatAfterFailedLoad(conversationId, meta?.cid)) return;
        setLive(null); liveRef.current = null; setStreamText(""); setStreaming(false); setLoading(false);
        setAttachments([]); setFocusedKey(null); resetHistory(); setConversationId(newConversationId());
      },
    });
  };

  const loadOlderMessages = () => loadOlder(conversationId, { onError: setError });

  const hideConversation = async (cid) => {
    try {
      await api.deleteConversation(session.tenant.id, cid);
      setConversations((prev) => prev.filter((c) => c.conversation_id !== cid));
      if (cid === conversationId) newConversation();
    } catch (e) {
      setError("Could not remove conversation");
    }
    setConfirmDelConv(null);
  };

  const newConversation = () => {
    abortRef.current?.abort();
    runIdRef.current++;
    liveRef.current = null;
    resetHistory();
    setLive(null); setStreamText(""); setStreaming(false); setLoading(false);
    setError(""); setAttachments([]); setFocusedKey(null);
    setConversationId(newConversationId());
    localStorage.removeItem(activeConvKey);
  };

  const stopStreaming = () => {
    if (!streaming && !loading) return;
    runIdRef.current++;
    abortRef.current?.abort();
    const base = liveRef.current;
    if (base) {
      const finished = {
        ...base, id: null, stopped: true, verified: false, verification_status: "not_applicable",
        steps: { ...base.steps, response: { ...base.steps.response, text: streamText || "" } },
      };
      setMessages((prev) => [...prev, finished]);
    }
    setLive(null); liveRef.current = null; setStreaming(false); setStreamText(""); setLoading(false);
  };

  const focusMessage = (key) => { setFocusedKey(key); setMobileTab("inspect"); };

  const beginReplaceAt = (attachmentId) => beginReplaceAtHelper(replaceIdRef, fileRef, attachmentId);

  const onFilesSelected = (fileList) =>
    handleComposerFilesSelected({
      fileList,
      replaceIdRef,
      fileRef,
      uploadFile: api.uploadFile,
      setAttachments,
      setUploading,
      setError,
    });

  const run = async (text) => {
    const p = (text ?? prompt).trim();
    const atts = attachments.map((a) => a.id);
    if (!p && atts.length === 0) return;
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    const myId = ++runIdRef.current;
    const isStale = () => runIdRef.current !== myId;
    const sentAtts = attachments;

    setError(""); setPrompt(""); setAttachments([]); setLoading(true);
    setLive(null); liveRef.current = null; setStreaming(false); setStreamText("");
    setFocusedKey("live"); setMobileTab("chat");
    try {
      const resp = await api.chatStream(
        { prompt: p, conversation_id: conversationId, attachments: atts },
        { signal: ctrl.signal },
      );
      if (!resp.ok || !resp.body) throw new Error("stream failed");
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let acc = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done || isStale()) break;
        buffer += decoder.decode(value, { stream: true });
        const blocks = buffer.split("\n\n");
        buffer = blocks.pop() || "";
        for (const block of blocks) {
          if (!block.trim() || isStale()) continue;
          const lines = block.split("\n");
          const evLine = lines.find((l) => l.startsWith("event:"));
          const dataLine = lines.find((l) => l.startsWith("data:"));
          if (!dataLine) continue;
          const ev = evLine ? evLine.slice(6).trim() : "message";
          const data = JSON.parse(dataLine.slice(5).trim());
          if (ev === "meta") {
            const r = data.steps.response;
            const willStream = !r.blocked && !r.held && !!data.steps.routing.chosen;
            const metaMsg = {
              ...data, prompt: p, attachmentsSent: sentAtts, id: null, verified: false, verified_by: null, verified_at: null,
              verification_status: willStream ? "unverified" : "not_applicable",
            };
            liveRef.current = metaMsg; setLive(metaMsg); setLoading(false); setStreaming(willStream);
          } else if (ev === "token") {
            acc += data.delta; setStreamText(acc);
          } else if (ev === "done") {
            const base = liveRef.current;
            if (!base) continue;
            const finished = {
              ...base, id: data.audit_id, verification_status: data.verification_status,
              verified: data.verification_status === "verified",
              steps: { ...base.steps, response: { ...base.steps.response, text: data.text ?? acc, final: data.final ?? null, latency_ms: data.latency_ms, simulated: data.simulated } },
            };
            setMessages((prev) => [...prev, finished]);
            setFocusedKey(data.audit_id);
            refreshConversations();
            localStorage.setItem(activeConvKey, conversationId);
            setLive(null); liveRef.current = null; setStreaming(false); setStreamText("");
          }
        }
      }
    } catch (e) {
      if (e?.name === "AbortError" || isStale()) return;
      setError("Gateway error");
    } finally {
      if (!isStale()) { setLoading(false); setStreaming(false); }
    }
  };

  const confirmReview = async (id) => {
    if (!id || verifyingId) return;
    setVerifyingId(id);
    try {
      const res = await api.verifyAudit(id);
      setMessages((prev) => prev.map((m) => (m.id === id ? { ...m, verified: true, verification_status: "verified", verified_by: res.verified_by, verified_at: res.verified_at } : m)));
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not record verification");
    } finally {
      setVerifyingId(null);
    }
  };

  const copyResponse = async (msg) => {
    if (!msg.verified) return;
    try {
      const r = msg.steps.response;
      const finalText = r.final != null ? r.final : "";
      if (!finalText) return;
      await navigator.clipboard.writeText(finalText);
      setCopiedId(msg.id); setTimeout(() => setCopiedId(null), 1800);
    } catch (e) { /* clipboard unavailable */ }
  };

  const threadItems = [
    ...messages.map((m) => ({ key: m.id, msg: m, live: false })),
    ...(live ? [{ key: "live", msg: live, live: true }] : []),
  ];
  const focusedItem =
    threadItems.find((ti) => String(ti.key) === String(focusedKey)) ||
    threadItems[threadItems.length - 1] || null;

  const AssistantBubble = ({ msg, isLive, focused }) => {
    const s = msg.steps;
    const r = s.response;
    const decision = s.policy.overall_applied;
    const provider = s.routing.chosen;
    const entities = (s.detection && s.detection.entities) || [];
    const respRaw = isLive ? streamText : r.text || "";
    const respFinal = isLive
      ? respRaw
      : (r.final != null ? r.final : "Final not available");
    const key = isLive ? "live" : msg.id;
    const showStreaming = isLive && streaming;
    const canVerify = !r.blocked && !r.held && !!msg.id;

    return (
      <div
        data-testid="chat-assistant-bubble"
        onClick={() => focusMessage(key)}
        className={`flex cursor-pointer gap-3 rounded-2xl transition-colors ${focused ? "ring-1 ring-brand/40" : ""}`}
      >
        <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-brand/40 bg-brand/10 text-brand">
          <ShieldCheck className="h-4 w-4" />
        </div>
        <div className={`min-w-0 flex-1 rounded-2xl rounded-tl-sm border p-4 ${focused ? "border-brand/40 bg-elevated/80" : "border-subtle bg-surface/70"}`}>
          <div className="mb-2 flex flex-wrap items-center gap-2">
            <ActionBadge action={decision} small />
            {r.masked_count > 0 && <Pill tone="mask"><Lock className="h-3 w-3" /> {r.masked_count} tokenized</Pill>}
            {provider && (provider.eu_sovereign ? <Pill tone="eu"><Globe className="h-3 w-3" /> {provider.name}</Pill> : <Pill tone="muted">{provider.name}</Pill>)}
            {canVerify && (
              msg.verified
                ? <span data-testid="verification-badge" className="inline-flex items-center gap-1 font-mono text-[10px] font-bold uppercase tracking-wider text-allow"><ShieldCheck className="h-3.5 w-3.5" /> Verified</span>
                : <span data-testid="verification-badge" className="inline-flex items-center gap-1 font-mono text-[10px] font-bold uppercase tracking-wider text-warn"><ShieldAlert className="h-3.5 w-3.5" /> Unverified</span>
            )}
            {!isLive && (
              <span data-testid="inspect-toggle" onClick={(e) => { e.stopPropagation(); focusMessage(key); }} className="ml-auto flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest text-slate-400 hover:text-brand">
                <ScanSearch className="h-3.5 w-3.5" /> Inspect
              </span>
            )}
          </div>

          {r.blocked ? (
            <div className="rounded-lg border border-block/40 bg-block/10 p-3" data-testid="blocked-response">
              <div className="flex items-center gap-2 text-sm font-semibold text-block"><ShieldAlert className="h-4 w-4" /> Request blocked before reaching any provider</div>
              <p className="mt-1 text-xs text-slate-400">No content left LucidGate.</p>
            </div>
          ) : r.held ? (
            <HeldResponseNotice msg={msg} />
          ) : (
            <div className="whitespace-pre-wrap text-sm leading-relaxed text-slate-200">
              {respFinal}
              {showStreaming && <span data-testid="streaming-indicator" className="ml-0.5 inline-block h-4 w-1.5 animate-pulse bg-brand align-middle" />}
            </div>
          )}

          {msg.stopped && (
            <div data-testid="stopped-note" className="mt-2 flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-slate-500">
              <Square className="h-3 w-3" /> Generation stopped — partial answer, not recorded or verified
            </div>
          )}

          {msg.remediation && (
            <div data-testid="remediation-panel" className="mt-3 rounded-lg border border-gold/30 bg-gold/10 p-3">
              <div className="flex items-center gap-2 text-xs font-semibold text-gold"><ShieldAlert className="h-3.5 w-3.5" /> How to proceed</div>
              <p className="mt-1 text-xs text-slate-300">{msg.remediation.suggestion}</p>
            </div>
          )}

          {canVerify && (
            <div data-testid="verification-gate" onClick={(e) => e.stopPropagation()} className={`mt-3 rounded-lg border p-2.5 ${msg.verified ? "border-allow/40 bg-allow/10" : "border-warn/40 bg-warn/10"}`}>
              <div className="flex items-center justify-between gap-3">
                {!msg.verified ? (
                  <label className="flex cursor-pointer items-start gap-2 text-xs text-slate-300">
                    <input type="checkbox" data-testid="verification-checkbox" checked={false} disabled={verifyingId === msg.id} onChange={() => confirmReview(msg.id)} className="mt-0.5 h-4 w-4 accent-brand" />
                    <span>{verifyingId === msg.id ? "Recording verification…" : "I confirm I have reviewed this output before using it."}</span>
                  </label>
                ) : (
                  <span className="font-mono text-[10px] text-slate-500">Reviewed by {msg.verified_by || session.user.name}{msg.verified_at ? ` · ${fmtTs(msg.verified_at)}` : ""}</span>
                )}
                <button data-testid="copy-response-btn" onClick={() => copyResponse(msg)} disabled={!msg.verified} title={msg.verified ? "Copy response" : "Confirm review to unlock"} className="flex shrink-0 items-center gap-1.5 rounded-lg border border-subtle px-2.5 py-1 text-xs text-slate-300 hover:border-highlight disabled:cursor-not-allowed disabled:opacity-40">
                  {copiedId === msg.id ? <ClipboardCheck className="h-3.5 w-3.5 text-allow" /> : <Copy className="h-3.5 w-3.5" />}
                  {copiedId === msg.id ? "Copied" : "Copy"}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const paneToggle = (
    <div className="mb-3 flex gap-1 rounded-xl border border-subtle bg-surface/70 p-1 lg:hidden">
      <button data-testid="mobile-tab-chat" onClick={() => setMobileTab("chat")} className={`flex-1 rounded-lg py-1.5 font-mono text-[10px] uppercase tracking-widest ${mobileTab === "chat" ? "bg-brand/15 text-brand" : "text-slate-400"}`}>Chat</button>
      <button data-testid="mobile-tab-inspect" onClick={() => setMobileTab("inspect")} className={`flex-1 rounded-lg py-1.5 font-mono text-[10px] uppercase tracking-widest ${mobileTab === "inspect" ? "bg-brand/15 text-brand" : "text-slate-400"}`}>Inspection</button>
    </div>
  );

  return (
    <div className="flex flex-col lg:h-[calc(100vh-8.5rem)] lg:min-h-[600px]">
      {paneToggle}
      <div className="grid min-h-0 flex-1 grid-cols-1 gap-4 lg:grid-cols-12">
        {/* LEFT — Chat */}
        <section className={`${mobileTab === "chat" ? "flex" : "hidden"} h-[72vh] min-h-0 flex-col overflow-hidden rounded-2xl border border-subtle bg-surface/70 backdrop-blur-md lg:col-span-7 lg:flex lg:h-full`}>
          <div data-testid="chat-pane-header" className="flex items-center justify-between gap-2 border-b border-subtle bg-surface/80 px-4 py-3 backdrop-blur">
            <div className="flex items-center gap-2">
              <MessageSquareLock className="h-4 w-4 text-brand" />
              <div>
                <div className="font-display text-sm font-bold text-white">Gateway Chat</div>
                <div className="flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-widest text-allow">
                  <span className="h-1.5 w-1.5 rounded-full bg-allow" /> Gateway active · {sector}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="relative">
                <button data-testid="conversations-btn" onClick={() => { setShowConvs((v) => !v); if (!showConvs) refreshConversations(); }} className="flex items-center gap-1.5 rounded-lg border border-subtle px-2.5 py-1.5 font-mono text-[10px] uppercase tracking-widest text-slate-300 hover:border-brand/50 hover:text-brand">
                  <MessageSquare className="h-3.5 w-3.5" /> <span className="hidden sm:inline">Conversations</span>
                  <ChevronDown className={`h-3.5 w-3.5 transition-transform ${showConvs ? "rotate-180" : ""}`} />
                </button>
                {showConvs && (
                  <div data-testid="conversations-list" className="absolute right-0 z-30 mt-2 max-h-80 w-80 overflow-auto rounded-xl border border-subtle bg-surface p-2 shadow-xl custom-scrollbar">
                    {conversations.length === 0 ? (
                      <div className="p-3 text-xs text-slate-500">No past conversations yet.</div>
                    ) : conversations.map((c) => (
                      <div key={c.conversation_id} data-testid="conversation-item" className={`group flex items-center gap-1 rounded-lg pr-1 transition-colors hover:bg-white/5 ${c.conversation_id === conversationId ? "bg-brand/10" : ""}`}>
                        <button data-testid="conversation-open" onClick={() => loadConversation(c.conversation_id)} className="flex min-w-0 flex-1 items-center gap-2 p-2.5 text-left">
                          <MessageSquare className="h-3.5 w-3.5 shrink-0 text-slate-500" />
                          <span className="min-w-0 flex-1">
                            <span className="block truncate text-xs text-slate-200">{c.title}</span>
                            <span className="font-mono text-[9px] uppercase tracking-wider text-slate-600">{c.turns} turn{c.turns === 1 ? "" : "s"} · {new Date(c.last_ts).toLocaleDateString("en-GB")}</span>
                          </span>
                        </button>
                        {confirmDelConv === c.conversation_id ? (
                          <span className="flex shrink-0 items-center gap-1">
                            <button data-testid="conversation-delete-confirm" title="Remove from my list (audit trail is kept)" onClick={(e) => { e.stopPropagation(); hideConversation(c.conversation_id); }} className="rounded-md border border-block/50 p-1.5 text-block hover:bg-block/10"><Check className="h-3.5 w-3.5" /></button>
                            <button data-testid="conversation-delete-cancel" onClick={(e) => { e.stopPropagation(); setConfirmDelConv(null); }} className="rounded-md border border-subtle p-1.5 text-slate-400 hover:text-white"><XCircle className="h-3.5 w-3.5" /></button>
                          </span>
                        ) : (
                          <button data-testid="conversation-delete-btn" title="Remove from my conversation list" onClick={(e) => { e.stopPropagation(); setConfirmDelConv(c.conversation_id); }} className="shrink-0 rounded-md p-1.5 text-slate-600 opacity-0 transition-opacity hover:text-block group-hover:opacity-100"><Trash2 className="h-3.5 w-3.5" /></button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {hasThread && (
                <button data-testid="new-conversation-btn" onClick={newConversation} title="New conversation" className="flex shrink-0 items-center gap-1.5 rounded-lg border border-subtle px-2.5 py-1.5 font-mono text-[10px] uppercase tracking-widest text-slate-300 hover:border-brand/50 hover:text-brand">
                  <MessageSquare className="h-3.5 w-3.5" /> <span className="hidden sm:inline">New</span>
                </button>
              )}
            </div>
          </div>

          {/* Thread */}
          <div data-testid="chat-thread" className="custom-scrollbar flex-1 space-y-5 overflow-y-auto p-4 sm:p-5">
            {historyHasMore && (
              <div className="flex justify-center">
                <button
                  type="button"
                  data-testid="load-older-messages"
                  onClick={loadOlderMessages}
                  disabled={historyLoading}
                  className="rounded-lg border border-subtle px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest text-slate-300 hover:border-brand/50 hover:text-brand disabled:opacity-40"
                >
                  {historyLoading ? "Loading…" : "Load older messages"}
                </button>
              </div>
            )}
            {!hasThread && !loading && (
              <div className="flex min-h-full flex-col items-center justify-center text-center">
                <div className="flex h-14 w-14 items-center justify-center rounded-2xl border border-subtle bg-elevated">
                  <MessageSquareLock className="h-7 w-7 text-brand" />
                </div>
                <p className="mt-4 font-display text-[15px] font-bold text-white">EU Sovereign AI Playground</p>
                <p className="mt-1 max-w-md text-sm text-slate-500">Type a prompt or attach a file to watch the gateway detect, mask, route to an EU-sovereign provider, and require human sign-off.</p>
                <div className="mt-5 w-full max-w-lg space-y-2">
                  {(GATEWAY_CHAT_EXAMPLES[sector] || []).map((ex, i) => {
                    const tm = TONE_META[ex.tone];
                    return (
                      <button key={i} data-testid={`example-${ex.tone}`} onClick={() => run(ex.text)} className="group flex w-full items-start gap-3 rounded-xl border border-subtle bg-surface/60 p-3 text-left transition-colors hover:border-highlight hover:bg-white/5">
                        <span className="mt-0.5 shrink-0 rounded-full px-2 py-0.5 font-mono text-[9px] font-bold uppercase tracking-wider" style={{ color: tm.color, background: `${tm.color}1a`, border: `1px solid ${tm.color}55` }}>{tm.label}</span>
                        <span className="text-xs leading-relaxed text-slate-300 group-hover:text-white">{ex.text}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {threadItems.map(({ key, msg, live: il }) => {
              const atts = (il
                ? msg.attachmentsSent
                : (msg.attachmentsSent || msg.steps?.attachments)
              ) || [];
              const typed = typedOnly(msg.prompt);
              return (
                <div key={key} data-testid="chat-message" className="space-y-3">
                  <div className="flex justify-end gap-3">
                    <div className="max-w-[85%]">
                      {(typed || atts.length > 0) && (
                        <div className="rounded-2xl rounded-tr-sm border border-subtle bg-elevated px-4 py-2.5 text-sm text-white" data-testid="chat-user-bubble">
                          {typed || <span className="text-slate-400">Attached file{atts.length > 1 ? "s" : ""} for review</span>}
                          {atts.length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-1.5">
                              {atts.map((a, i) => <AttachmentChip key={i} att={a} testid="attachment-chip-sent" />)}
                            </div>
                          )}
                        </div>
                      )}
                      {!il && typed && (
                        <div className="mt-1 flex justify-end">
                          <button data-testid="reuse-prompt" onClick={() => { setPrompt(typed); document.querySelector('[data-testid="gateway-prompt-input"]')?.focus(); }} className="flex items-center gap-1 font-mono text-[9px] uppercase tracking-widest text-slate-500 hover:text-brand">
                            <Copy className="h-3 w-3" /> Reuse prompt
                          </button>
                        </div>
                      )}
                    </div>
                    <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-subtle bg-elevated text-slate-400"><User className="h-4 w-4" /></div>
                  </div>
                  <AssistantBubble msg={msg} isLive={il} focused={focusedItem && String(focusedItem.key) === String(key)} />
                </div>
              );
            })}

            {loading && !live && (
              <div className="flex items-center gap-2 pl-11 font-mono text-xs uppercase tracking-widest text-slate-500"><Loader2 className="h-4 w-4 animate-spin text-brand" /> Inspecting · classifying · routing…</div>
            )}
            {error && <div className="rounded-lg border border-block/40 bg-block/10 px-3 py-2 text-sm text-block">{error}</div>}
            <div ref={endRef} />
          </div>

          {/* Composer */}
          <div className="border-t border-subtle bg-surface/95 p-3 backdrop-blur sm:p-4">
            {hasThread && (
              <div className="mb-2 flex flex-wrap gap-1.5">
                {(GATEWAY_CHAT_EXAMPLES[sector] || []).map((ex) => {
                  const tm = TONE_META[ex.tone];
                  return (
                    <button key={ex.tone} data-testid={`example-${ex.tone}`} disabled={loading || streaming} onClick={() => run(ex.text)} className="rounded-full border px-2.5 py-1 font-mono text-[9px] font-bold uppercase tracking-wider transition-colors disabled:opacity-40" style={{ color: tm.color, borderColor: `${tm.color}55`, background: `${tm.color}12` }}>{tm.label}</button>
                  );
                })}
              </div>
            )}

            <ComposerAttachments
              attachments={attachments}
              uploading={uploading}
              setAttachments={setAttachments}
              onBeginReplace={beginReplaceAt}
            />

            <div className="flex items-end gap-2 rounded-2xl border border-subtle bg-elevated p-2">
              <input ref={fileRef} data-testid="file-input" type="file" multiple accept=".png,.jpg,.jpeg,.webp,.gif,.bmp,.tiff,.pdf,.docx,.xlsx,.csv,.txt,.json,.md,image/*,application/pdf" className="hidden" onChange={(e) => onFilesSelected(e.target.files)} />
              <button data-testid="file-attach-btn" onClick={() => { clearReplaceIntent(replaceIdRef); fileRef.current?.click(); }} disabled={loading || streaming || uploading} title="Attach a file (image, PDF, Word, CSV, Excel)" className="mb-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-subtle text-slate-400 transition-colors hover:border-brand/50 hover:text-brand disabled:opacity-40">
                <Paperclip className="h-4 w-4" />
              </button>
              <textarea
                data-testid="gateway-prompt-input"
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); run(); } }}
                rows={1}
                placeholder="Type a prompt, or attach a file…  (Enter to send, Shift+Enter for newline)"
                className="custom-scrollbar max-h-32 min-h-[40px] flex-1 resize-none bg-transparent px-2 py-2 text-sm outline-none"
              />
              {streaming ? (
                <button data-testid="stop-stream-btn" onClick={stopStreaming} className="flex items-center gap-1.5 rounded-lg bg-block px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-rose-500"><Square className="h-4 w-4" /> Stop</button>
              ) : (
                <Btn data-testid="submit-prompt-btn" onClick={() => run()} disabled={loading || uploading || (!prompt.trim() && attachments.length === 0)}>
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send
                </Btn>
              )}
            </div>
            <p className="mt-1.5 px-1 font-mono text-[10px] text-slate-600">Files are extracted on-server (OCR/parse) before scanning · prompts logged to the audit trail · human verification required before copy.</p>
          </div>
        </section>

        {/* RIGHT — Inspector */}
        <section data-testid="inspect-pane" className={`${mobileTab === "inspect" ? "flex" : "hidden"} h-[72vh] min-h-0 flex-col overflow-hidden rounded-2xl border border-subtle bg-surface/70 backdrop-blur-md lg:col-span-5 lg:flex lg:h-full`}>
          <Inspector item={focusedItem} streamText={streamText} streaming={streaming} />
        </section>
      </div>
    </div>
  );
}
